// How the result of going live is reported to an agent (CWA-154 finding 7).
//
// Going live has two outcomes that fail independently: the confirmation e-mail,
// and granting the customer ownership of their own stations. The ownership grant
// used to be invisible — the screen reported success on a response that said the
// grant had failed, and the customer was left unable to see their own station.
//
// One function composes both results into one message, so an agent cannot
// dismiss the first of two toasts and miss the second.

import { GoLiveResponse, OwnerPermissionStatus } from "../types/onboarding";

export interface GoLiveReport {
  severity: "success" | "warn";
  summary: string;
  detail?: string;
}

interface ReportParams {
  result: GoLiveResponse | null | undefined;
  // How many stations the prospect has, so the copy agrees with reality.
  stationCount: number;
  // A resend changes nothing about the stations; it only sends the e-mail again.
  resend?: boolean;
}

// At most this many failed stations are named; the rest are counted. One broken
// integration must not produce a toast nobody can read.
const MAX_NAMED_FAILURES = 3;

const failureList = (failures: string[]): string => {
  const named = failures.slice(0, MAX_NAMED_FAILURES).join("; ");
  const rest = failures.length - MAX_NAMED_FAILURES;
  return rest > 0 ? `${named}; and ${rest} more` : named;
};

// Absent is not failed. A backend that does not report ownership tells us
// nothing, and inventing a failure would be as wrong as inventing a success.
const ownershipFailed = (status?: OwnerPermissionStatus): boolean =>
  status === "PARTIAL" || status === "FAILED";

export const goLiveReport = ({
  result,
  stationCount,
  resend = false,
}: ReportParams): GoLiveReport => {
  const many = stationCount > 1;
  const noun = many ? "stations" : "station";
  const verb = many ? "are" : "is";

  const emailFailed = result?.email_status === "FAILED";
  const ownerStatus = result?.owner_permission_status;
  const failures = result?.owner_permission_failures ?? [];

  // The stations going live is stated in every branch below: it happened, and a
  // warning about something else must not read as if the action failed.
  const ownershipDetail = () => {
    if (ownerStatus === "PARTIAL") {
      return failures.length
        ? `The customer cannot access these stations yet: ${failureList(failures)}.`
        : "The customer can only access some of their stations so far.";
    }
    return failures.length
      ? `The customer has no access to their ${noun} yet: ${failureList(failures)}.`
      : `The customer has no access to their ${noun} yet.`;
  };

  if (resend) {
    // A resend was asked to send an e-mail, nothing else. Report on that — and
    // on ownership only when it went wrong, since nobody asked for it.
    if (emailFailed) {
      return {
        severity: "warn",
        summary: "Email not sent",
        detail: "The email could not be sent. Please try again.",
      };
    }
    if (ownershipFailed(ownerStatus)) {
      return {
        severity: "warn",
        summary: "Email sent again, but station access is still missing",
        detail: ownershipDetail(),
      };
    }
    return { severity: "success", summary: "Confirmation email sent again." };
  }

  if (emailFailed && ownershipFailed(ownerStatus)) {
    return {
      severity: "warn",
      summary: `The ${noun} ${verb} live, but the email and the station access did not go through`,
      detail: `The confirmation email could not be sent. ${ownershipDetail()}`,
    };
  }

  if (emailFailed) {
    return {
      severity: "warn",
      summary: `The ${noun} ${verb} live, but no email went out`,
      detail: `The ${noun} ${verb} live, but the confirmation email could not be sent. Try sending it again.`,
    };
  }

  if (ownershipFailed(ownerStatus)) {
    return {
      severity: "warn",
      summary: `The ${noun} ${verb} live, but the customer cannot access ${
        many ? "them all" : "it"
      } yet`,
      detail: `${ownershipDetail()} The customer has been notified that their ${noun} ${verb} live.`,
    };
  }

  return {
    severity: "success",
    summary: `The ${noun} ${verb} live and the customer has been notified.`,
  };
};
